import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PricingHero from './PricingHero';
import WebsitePackages from './WebsitePackages';
import AppPackages from './AppPackages';
import CompanyOfferCard from './CompanyOfferCard';
import FounderSection from './FounderSection';
import PaymentTerms from './PaymentTerms';
import PricingCTA from './PricingCTA';
import { useWelcomeOffer } from '../../context/WelcomeOfferContext';
import { smoothScrollTo } from '../../utils/smoothScroll';
import { SANS } from '../../constants/fonts';

const TAB_ORDER = ['websites', 'offer', 'apps'];

function PricingScreen() {
  const { offer } = useWelcomeOffer();
  const location = useLocation();
  const hasOffer = !!(offer && offer.offerType);
  const [tab, setTab] = useState(hasOffer ? 'offer' : 'websites');
  const [visibleTab, setVisibleTab] = useState(hasOffer ? 'offer' : 'websites');
  // 'idle' = settled; 'exiting' = old content sliding/fading out;
  // 'entering' = new content has just swapped in but is still offset,
  // about to animate to its resting position.
  const [transitionState, setTransitionState] = useState('idle');
  const [direction, setDirection] = useState(1);
  const [addOnsExpanded, setAddOnsExpanded] = useState(false);
  const switchTimeout = useRef(null);
  const tabBarRef = useRef(null);

  useEffect(() => {
    // Depends on location.key (not just mount) because clicking the banner
    // while already on /pricing doesn't remount this component — only a
    // fresh navigation entry, which still needs to trigger the scroll.
    if (!hasOffer || !location.state?.scrollToOffer) return;
    setTab('offer');
    setVisibleTab('offer');
    setDirection(1);
    setTransitionState('entering');
    const enterFrame = requestAnimationFrame(() => setTransitionState('idle'));

    const frame = requestAnimationFrame(() => {
      const target = tabBarRef.current;
      if (!target) return;
      const top = target.getBoundingClientRect().top + window.scrollY;
      smoothScrollTo(top);
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(enterFrame);
    };
  }, [location.key, hasOffer, location.state?.scrollToOffer]);

  const handleTabSwitch = (newTab) => {
    if (newTab === tab) return;
    setDirection(TAB_ORDER.indexOf(newTab) > TAB_ORDER.indexOf(tab) ? 1 : -1);
    clearTimeout(switchTimeout.current);
    setTransitionState('exiting');
    switchTimeout.current = setTimeout(() => {
      setTab(newTab);
      setVisibleTab(newTab);
      setAddOnsExpanded(false);
      setTransitionState('entering');
      requestAnimationFrame(() => setTransitionState('idle'));
    }, 200);
  };

  const goToCarePlans = (category) => {
    const targetTab = category === 'app' ? 'apps' : 'websites';
    const targetId = targetTab === 'apps' ? 'app-care-plans' : 'web-care-plans';
    const scrollToSection = () => {
      const el = document.getElementById(targetId);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY - (window.innerHeight - rect.height) / 2;
      smoothScrollTo(top);
    };
    if (tab === targetTab) {
      scrollToSection();
    } else {
      handleTabSwitch(targetTab);
      setTimeout(scrollToSection, 260);
    }
  };

  const contentStyle = transitionState === 'exiting'
    ? { maxWidth: 960, margin: '0 auto', opacity: 0, transform: `translateX(${-direction * 24}px)`, transition: 'opacity 0.2s ease, transform 0.2s ease' }
    : transitionState === 'entering'
    ? { maxWidth: 960, margin: '0 auto', opacity: 0, transform: `translateX(${direction * 24}px)`, transition: 'none' }
    : { maxWidth: 960, margin: '0 auto', opacity: 1, transform: 'translateX(0)', transition: 'opacity 0.25s ease, transform 0.25s ease' };

  return (
    <>
      <style>{`
        .pricing-card { transition: transform 0.18s ease, border-color 0.18s ease; }
        .pricing-card:hover { transform: translateY(-4px); }
        .contact-btn:hover { opacity: 0.82 !important; }
        .care-plan-pill:hover { background: rgba(129,140,248,0.16) !important; border-color: rgba(129,140,248,0.5) !important; }
        @media (max-width: 720px) {
          .cards-grid { grid-template-columns: 1fr !important; }
          .care-grid  { grid-template-columns: 1fr !important; }
          .build-row  { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={s.page}>

        <PricingHero />

        {/* ── Tab switcher ──────────────────────────────────────── */}
        <div style={s.tabBar} ref={tabBarRef}>
          <div style={s.tabTrack}>
            <button
              onClick={() => handleTabSwitch('websites')}
              style={{ ...s.tabBtn, ...(tab === 'websites' ? s.tabBtnActive : {}) }}
            >
              Websites
            </button>
            {hasOffer && (
              <button
                onClick={() => handleTabSwitch('offer')}
                style={{ ...s.tabBtn, ...(tab === 'offer' ? s.tabBtnActive : {}) }}
              >
                {offer.name}
              </button>
            )}
            <button
              onClick={() => handleTabSwitch('apps')}
              style={{ ...s.tabBtn, ...(tab === 'apps' ? s.tabBtnActive : {}) }}
            >
              Apps
            </button>
          </div>
        </div>

        <div style={{ background: '#0f0f13', padding: '36px 24px clamp(64px, 8vw, 104px)' }}>
          <div style={contentStyle}>
            {visibleTab === 'websites' && <WebsitePackages />}
            {visibleTab === 'offer' && hasOffer && (
              <CompanyOfferCard offer={offer} onCarePlanClick={goToCarePlans} />
            )}
            {visibleTab === 'apps' && (
              <AppPackages
                addOnsExpanded={addOnsExpanded}
                setAddOnsExpanded={setAddOnsExpanded}
              />
            )}
          </div>
        </div>

        <FounderSection />

        <PaymentTerms />

        <PricingCTA />

      </div>
    </>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f9f9f7',
    color: '#2a2a2a',
    fontFamily: SANS,
  },
  tabBar: {
    background: '#0f0f13',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 24px 0',
  },
  tabTrack: {
    display: 'inline-flex',
    background: '#1c1c26',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: 4,
    gap: 4,
  },
  tabBtn: {
    fontFamily: SANS,
    fontSize: '0.88rem',
    fontWeight: 600,
    padding: '9px 28px',
    borderRadius: 9,
    border: 'none',
    background: 'transparent',
    color: '#9096b0',
    cursor: 'pointer',
    transition: 'background 0.18s ease, color 0.18s ease',
    letterSpacing: '0.01em',
    WebkitTapHighlightColor: 'transparent',
  },
  tabBtnActive: {
    background: '#4338ca',
    color: '#fff',
    boxShadow: '0 2px 10px rgba(67,56,202,0.5)',
  },
};

export default PricingScreen;
